/**
 * 🎲 Tilt3D — a tiny CSS-only 3D tilt wrapper.
 *
 * Wraps any 2D art (hero sprite, mount, item icon, chit bag…) and
 * gives it a parallax tilt on pointer-move + a lifted drop shadow,
 * so flat pictures sit *in* space instead of lying on the page.
 *
 * Zero deps, no WebGL, respects reduced-motion.
 */
import { useRef, useState, useCallback } from "react";

export default function Tilt3D({
  children,
  max = 14,          // max degrees of tilt
  scale = 1.04,      // slight pop on hover
  glare = true,      // optional specular sweep
  className = "",
  style = {},
  testid,
}) {
  const ref = useRef(null);
  const [tx, setTx] = useState({ rx: 0, ry: 0, px: 50, py: 50, active: false });

  const onMove = useCallback(
    (e) => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width;   // 0..1
      const y = (e.clientY - r.top) / r.height;   // 0..1
      const ry = (x - 0.5) * (max * 2);            // left/right
      const rx = -(y - 0.5) * (max * 2);           // up/down
      setTx({ rx, ry, px: x * 100, py: y * 100, active: true });
    },
    [max]
  );

  const onLeave = useCallback(() => {
    setTx({ rx: 0, ry: 0, px: 50, py: 50, active: false });
  }, []);

  const transform = `perspective(700px) rotateX(${tx.rx}deg) rotateY(${tx.ry}deg) scale(${tx.active ? scale : 1})`;

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      onTouchStart={onLeave}
      className={`tilt3d-root ${className}`}
      style={{
        transform,
        transformStyle: "preserve-3d",
        transition: tx.active
          ? "transform 60ms linear"
          : "transform 320ms cubic-bezier(.2,.9,.2,1)",
        willChange: "transform",
        filter: tx.active
          ? "drop-shadow(0 18px 22px rgba(0,0,0,.55)) drop-shadow(0 4px 8px rgba(255,180,60,.18))"
          : "drop-shadow(0 10px 14px rgba(0,0,0,.45))",
        ...style,
      }}
      data-testid={testid}
    >
      {children}
      {glare && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-[inherit] mix-blend-screen"
          style={{
            background: `radial-gradient(circle at ${tx.px}% ${tx.py}%, rgba(255,220,140,0.22), rgba(255,220,140,0) 55%)`,
            opacity: tx.active ? 1 : 0,
            transition: "opacity 240ms ease",
          }}
        />
      )}
    </div>
  );
}
